import { motion, useReducedMotion, useSpring, useTransform, type MotionValue } from "motion/react";

/**
 * The character in the hero — an engineer training a model.
 *
 * Drawn as inline SVG rather than shipped as an image: it recolours with the
 * theme for free (every stroke is `currentColor` or a palette variable, so
 * there is no second asset for dark), it stays sharp at any size without a 2x
 * file, and it costs no network request in the hero's critical path.
 *
 * **What is on the screen is the point.** A laptop with three anonymous code
 * lines is a stock illustration of "person types". This one shows a small
 * network with activation sweeping through it and a loss curve falling — the
 * two things you actually watch while a model trains. It also rhymes with the
 * WebGL field behind it rather than introducing a second unrelated idea.
 *
 * It is `aria-hidden`. Decoration beside a headline that already says
 * everything; describing it to a screen reader would add nothing.
 *
 * Every idle motion runs on its own period. Anything looping in lockstep
 * reads as a loading spinner — offsetting them is most of what makes a
 * drawing feel alive rather than animated.
 */

/** Input, hidden, output — enough to read as a network at this size. */
const SCREEN_NODES = [
  { x: 126, y: 190 },
  { x: 126, y: 206 },
  { x: 126, y: 222 },
  { x: 154, y: 186 },
  { x: 154, y: 206 },
  { x: 154, y: 226 },
  { x: 182, y: 198 },
  { x: 182, y: 214 },
] as const;

const SCREEN_EDGES: [number, number][] = [
  [0, 3],
  [0, 4],
  [1, 4],
  [1, 5],
  [2, 4],
  [2, 5],
  [3, 6],
  [4, 6],
  [4, 7],
  [5, 7],
];

export function EngineerCharacter({
  scrollProgress,
  pointerX,
  pointerY,
}: {
  scrollProgress: MotionValue<number>;
  pointerX: MotionValue<number>;
  pointerY: MotionValue<number>;
}) {
  const reduced = useReducedMotion() ?? false;

  // Slower than the headline on scroll. Matching rates would collapse the
  // depth the whole composition depends on.
  const drift = useTransform(scrollProgress, [0, 1], [0, 80]);
  const fade = useTransform(scrollProgress, [0, 0.8], [1, 0]);

  // Leans *toward* the pointer, softly. The slack spring is the point — it
  // arrives late, which reads as weight rather than as a value being set.
  const leanX = useSpring(useTransform(pointerX, [-0.5, 0.5], [-16, 16]), {
    stiffness: 45,
    damping: 18,
  });
  const leanY = useSpring(useTransform(pointerY, [-0.5, 0.5], [-10, 10]), {
    stiffness: 45,
    damping: 18,
  });

  return (
    <motion.div
      aria-hidden="true"
      style={reduced ? undefined : { y: drift, opacity: fade, x: leanX }}
      // z-20 puts him ABOVE the hero's veil. Rendered under it he was washed
      // out by 42% of the page colour — that veil exists to knock the network
      // back, and he is not the network.
      //
      // Responsive notes: hidden until `lg`, then sized as a share of the
      // hero so he scales with the viewport instead of a fixed pixel block.
      // The `max-height` variant shrinks him on short laptop screens where a
      // full-height figure would crowd the headline. `items-end` on small
      // heights keeps his base (the desk line) anchored near the bottom.
      className="pointer-events-none absolute inset-y-0 right-0 z-20 hidden w-[46%] items-center justify-center lg:flex"
    >
      <motion.div
        style={reduced ? undefined : { y: leanY }}
        className="relative w-[min(30rem,90%)] max-lg:[&_svg]:max-h-[70vh] [@media(max-height:820px)]:w-[min(24rem,80%)]"
      >
        {/* The light he sits in. Blurred, so it has no edge. */}
        <div className="absolute inset-0 -z-10 scale-125 rounded-full bg-[radial-gradient(50%_50%_at_50%_50%,color-mix(in_oklab,var(--color-glow)_42%,transparent),transparent_70%)] blur-2xl" />

        <motion.svg
          viewBox="0 0 320 300"
          className="mx-auto block h-auto w-full max-h-[min(38rem,78svh)] text-text"
          animate={reduced ? undefined : { y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
            {/* ── Desk ──────────────────────────────────────────────── */}
            <path d="M40 258h240" strokeWidth="3" opacity="0.4" />

            {/* ── Training data, streaming into the laptop ──────────── */}
            {!reduced &&
              [0, 1, 2].map((lane) => (
                <motion.circle
                  key={`data-${String(lane)}`}
                  r="3"
                  fill="var(--color-accent)"
                  stroke="none"
                  animate={{
                    cx: [44, 112],
                    cy: [150 + lane * 26, 196 + lane * 8],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 2.6,
                    delay: lane * 0.8,
                    repeat: Infinity,
                    ease: "easeIn",
                  }}
                />
              ))}

            {/* ── Laptop ────────────────────────────────────────────── */}
            <motion.rect
              x="112"
              y="176"
              width="96"
              height="58"
              rx="5"
              fill="var(--color-accent)"
              stroke="none"
              opacity="0.16"
              animate={reduced ? undefined : { opacity: [0.12, 0.26, 0.12] }}
              transition={{ duration: 3.7, repeat: Infinity, ease: "easeInOut" }}
            />
            <rect x="112" y="176" width="96" height="58" rx="5" strokeWidth="3" />

            {/* The model on the screen. Same idea as the field behind him. */}
            <g stroke="var(--color-accent)" strokeWidth="1.2" opacity="0.6">
              {SCREEN_EDGES.map(([a, b]) => (
                <line
                  key={`e${String(a)}-${String(b)}`}
                  x1={SCREEN_NODES[a].x}
                  y1={SCREEN_NODES[a].y}
                  x2={SCREEN_NODES[b].x}
                  y2={SCREEN_NODES[b].y}
                />
              ))}
            </g>
            <g fill="var(--color-accent)" stroke="none">
              {SCREEN_NODES.map((node, index) => (
                <motion.circle
                  key={`n${String(index)}`}
                  cx={node.x}
                  cy={node.y}
                  r="2.4"
                  animate={reduced ? undefined : { opacity: [0.45, 1, 0.45] }}
                  transition={{
                    duration: 2.2,
                    // Staggered by column, so activation sweeps forward
                    // through the layers rather than flashing all at once.
                    delay: ((node.x - 126) / 28) * 0.35,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </g>

            {/* Loss curve, falling and redrawing — the other thing you watch. */}
            <motion.path
              d="M120 230c14-2 22-10 30-16s16-9 34-10"
              stroke="var(--color-glow)"
              strokeWidth="2"
              animate={reduced ? undefined : { pathLength: [0, 1] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />

            <path d="M100 234h120l8 12H92l8-12Z" strokeWidth="3" />

            {/* ── Torso and arms ────────────────────────────────────── */}
            <path d="M118 176c0-24 18-38 42-38s42 14 42 38" strokeWidth="3" />
            <path d="M124 158c-14 6-20 18-18 30" strokeWidth="3" />
            <path d="M196 158c14 6 20 18 18 30" strokeWidth="3" />

            {/* ── Head ──────────────────────────────────────────────────
                Filled with the page colour so the network behind him does
                not show through his face — that was most of why he read as
                faded rather than as a figure in front of something. */}
            <rect
              x="128"
              y="72"
              width="64"
              height="66"
              rx="26"
              strokeWidth="3"
              fill="var(--color-canvas)"
            />
            <path d="M150 136v8m20-8v8" strokeWidth="3" />
            <path d="M136 84c6-12 20-18 34-16 10 1 18 7 20 16" strokeWidth="3" />
            <path d="M172 68c4-4 10-4 13 0" strokeWidth="3" opacity="0.75" />

            {/* ── Headphones ────────────────────────────────────────── */}
            <path d="M124 106V96a36 36 0 0 1 72 0v10" strokeWidth="3" />
            <rect
              x="116"
              y="102"
              width="14"
              height="24"
              rx="6"
              strokeWidth="3"
              fill="var(--color-canvas)"
            />
            <rect
              x="190"
              y="102"
              width="14"
              height="24"
              rx="6"
              strokeWidth="3"
              fill="var(--color-canvas)"
            />

            {/* ── Glasses, lit by the screen ────────────────────────── */}
            <motion.g
              animate={reduced ? undefined : { opacity: [0.16, 0.32, 0.16] }}
              transition={{ duration: 3.7, repeat: Infinity, ease: "easeInOut" }}
            >
              <rect x="136" y="98" width="22" height="18" rx="7" fill="var(--color-accent)" />
              <rect x="162" y="98" width="22" height="18" rx="7" fill="var(--color-accent)" />
            </motion.g>
            <rect x="136" y="98" width="22" height="18" rx="7" strokeWidth="3" />
            <rect x="162" y="98" width="22" height="18" rx="7" strokeWidth="3" />
            <path d="M158 106h4" strokeWidth="3" />

            {/* Eyes. A DOUBLE blink on a short period — one slow blink reads
                as a glitch, two in quick succession reads as a person. */}
            <motion.g
              fill="currentColor"
              stroke="none"
              animate={reduced ? undefined : { scaleY: [1, 1, 0.08, 1, 0.08, 1] }}
              transition={{
                duration: 3.4,
                repeat: Infinity,
                times: [0, 0.72, 0.78, 0.84, 0.9, 1],
                ease: "easeInOut",
              }}
              style={{ transformOrigin: "160px 107px" }}
            >
              <circle cx="147" cy="107" r="3" />
              <circle cx="173" cy="107" r="3" />
            </motion.g>

            <path d="M152 124c4 4 12 4 16 0" strokeWidth="3" />
          </g>
        </motion.svg>
      </motion.div>
    </motion.div>
  );
}
